import { get, post, put, upload } from "@/api";

export interface PurchaseCategorySummary {
  category_id?: number | string;
  category_name?: string;
  plan_quantity?: number;
  plan_amount?: number | string;
}

export interface PurchasePlanRecord {
  id: number | string;
  apply_month?: string | number;
  apply_month_text?: string;
  category_id?: number | string;
  category_name?: string;
  status?: number | string;
  status_text?: string;
  account_id?: number | string;
  created_at?: string;
  limit_time_start?: string;
  limit_time_end?: string;
  plan_quantity?: number;
  plan_amount?: number | string;
  category_summary?: PurchaseCategorySummary[];
  office?: { id?: number | string; title?: string } | null;
  member?: { id?: number | string; nickname?: string; account?: string } | null;
  instan?: Array<{
    id?: number | string;
    sort?: number | string;
    step_name?: string;
    step?: string;
    account_ids?: string | null;
    account_name?: string | null;
    account_names?: string | null;
    approve_state?: number | string;
    approve?: string | null;
    approve_time?: string | null;
    now_step_sort?: number | string;
    is_over?: number | string;
  }>;
}

export interface PurchasePlanPagesParams {
  page?: number;
  pagenumber?: number;
  year?: number | string;
  month?: string;
  category_id?: number | string;
  status?: number | number[];
  /** 科室筛选（is_divide 账号） */
  department_ids?: Array<number | string>;
  office_ids?: Array<number | string>;
  /** 我的待办（待我审批的采购计划） */
  is_todo?: boolean;
}

export interface PurchasePlanPagesResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: PurchasePlanRecord[];
  total?: number;
  page?: number;
  pagenumber?: number;
}

/** 科室采购计划列表 */
export function getPurchasePlanPages(params: PurchasePlanPagesParams = {}) {
  return post<PurchasePlanPagesResponse>("/platform/purchase/pages", params);
}

export interface PurchasePlanGoodsRow {
  flow_item_id?: number | string;
  control_type?: number | string;
  control_type_text?: string | null;
  /** config=分类配置（历史）；goods=商品清单 */
  item_mode?: 'config' | 'goods' | string;
  /** 历史分类配置：仅金额控制 */
  amount_only?: boolean;
  category_id?: number | string;
  category_name?: string | null;
  department_id?: number | string;
  department_name?: string | null;
  account_id?: number | string;
  account_name?: string | null;
  config_title?: string | null;
  config_desc?: string | null;
  goods_id?: number | string;
  spec_id?: number | string;
  goods_name?: string | null;
  goods_pic?: string | null;
  spec_name?: string | null;
  code?: string | null;
  unit?: string | null;
  plan_quantity?: number | string;
  /** 全院计划数：同月全院限购对应规格/商品的计划量 */
  hospital_plan_quantity?: number | string;
  used_quantity?: number | string;
  return_quantity?: number | string;
  remain_quantity?: number | string;
  price?: number | string | null;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  /** 已使用比例（百分比） */
  usage_ratio?: number | string;
  remark?: string | null;
  /** @deprecated 兼容旧字段，等同净用量 used - return */
  sale_quantity?: number | string;
  /** @deprecated 兼容旧字段，等同 remain_quantity */
  limit_quantity?: number | string;
  sale_amount?: number | string;
  goods?: { id?: number | string; title?: string; m_pic?: string } | null;
  spec?: {
    id?: number | string;
    name?: string;
    unit?: string;
    sku?: string;
  } | null;
}

/** 按 control_type 归组的计划明细 */
export interface PurchasePlanItemGroup {
  control_type: number | string;
  control_type_text?: string | null;
  item_mode?: 'config' | 'goods' | string;
  amount_only?: boolean;
  item_count?: number;
  plan_quantity?: number | string;
  used_quantity?: number | string;
  return_quantity?: number | string;
  remain_quantity?: number | string;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  usage_ratio?: number | string;
  items?: PurchasePlanGoodsRow[];
}

export interface PurchasePlanGoodsResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: {
    flow?: PurchasePlanRecord;
    /** 按 control_type 归组统计 */
    groups?: PurchasePlanItemGroup[];
    /** 兼容旧扁平列表 */
    goods?: PurchasePlanGoodsRow[];
  };
}

/** 采购计划物资明细 */
export function getPurchasePlanGoods(
  flowId: number | string,
  params: { category_id?: number | string } = {},
) {
  const payload: Record<string, unknown> = { flow_id: flowId };
  if (params.category_id != null && params.category_id !== "") {
    payload.category_id = params.category_id;
  }
  return post<PurchasePlanGoodsResponse>("/platform/purchase/goods", payload);
}

/** 当月全院限购计划数量（按规格 / 不限规格商品） */
export interface EnterprisePlanQuantityMap {
  spec?: Record<string, number>;
  goods?: Record<string, number>;
}

export interface EnterprisePlanQuantitiesResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: {
    apply_month?: string;
    spec?: Record<string, number>;
    goods?: Record<string, number>;
  };
}

/** 查询当月全院限购计划数，供科室限购对照 */
export function getEnterprisePlanQuantities(month: string) {
  return post<EnterprisePlanQuantitiesResponse>("/platform/purchase/enterprise-quantities", {
    month,
  });
}

/**
 * 匹配全院计划数：限定规格优先同 spec_id，否则回退该商品不限规格计划
 */
export function lookupHospitalPlanQuantity(
  specId: number | string | undefined,
  goodsId: number | string | undefined,
  map: EnterprisePlanQuantityMap | null | undefined,
): number {
  if (!map) return 0;
  const sid = Number(specId) || 0;
  const gid = Number(goodsId) || 0;
  if (sid > 0) {
    const specQty = map.spec?.[String(sid)];
    if (specQty != null) return Number(specQty) || 0;
    if (gid > 0) return Number(map.goods?.[String(gid)] ?? 0) || 0;
    return 0;
  }
  if (gid > 0) return Number(map.goods?.[String(gid)] ?? 0) || 0;
  return 0;
}

export function hospitalPlanExceedMessage(
  name: string,
  deptQty: number,
  hospitalQty: number,
): string {
  const label = name.trim() ? `物资「${name.trim()}」` : '';
  return `${label}科室计划数不能大于全院计划数（科室 ${deptQty} / 全院 ${hospitalQty}）`;
}

/** 科室本月采购计划汇总（分类维度） */
export interface PurchaseThisMonthCategorySummary {
  category_id?: number | string;
  category_name?: string | null;
  item_count?: number;
  plan_quantity?: number | string;
  used_quantity?: number | string;
  return_quantity?: number | string;
  remain_quantity?: number | string;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  usage_ratio?: number | string;
}

/** 科室本月采购计划总览 */
export interface PurchaseThisMonthSummary {
  item_count?: number;
  plan_quantity?: number | string;
  used_quantity?: number | string;
  return_quantity?: number | string;
  remain_quantity?: number | string;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  usage_ratio?: number | string;
}

/** 本月计划外采购明细行 */
export interface PurchaseOutOfPlanItem {
  goods_id?: number | string;
  spec_id?: number | string;
  goods_name?: string | null;
  spec_name?: string | null;
  code?: string | null;
  unit?: string | null;
  quantity?: number | string;
  price?: number | string;
  amount?: number | string;
}

/** 本月计划外分类汇总 */
export interface PurchaseOutOfPlanCategorySummary {
  category_id?: number | string;
  category_name?: string | null;
  goods_count?: number;
  quantity?: number | string;
  amount?: number | string;
}

/** 本月计划外采购统计 */
export interface PurchaseOutOfPlan {
  summary?: {
    goods_count?: number;
    quantity?: number | string;
    amount?: number | string;
  };
  category_summary?: PurchaseOutOfPlanCategorySummary[];
  items?: PurchaseOutOfPlanItem[];
}

export interface PurchaseThisMonthData {
  apply_month?: string | number;
  apply_month_text?: string;
  enterprise_id?: number | string;
  department_id?: number | string;
  department_name?: string | null;
  account_id?: number | string;
  account_name?: string | null;
  summary?: PurchaseThisMonthSummary;
  category_summary?: PurchaseThisMonthCategorySummary[];
  groups?: PurchasePlanItemGroup[];
  goods?: PurchasePlanGoodsRow[];
  out_of_plan?: PurchaseOutOfPlan;
}

export interface PurchaseThisMonthResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: PurchaseThisMonthData;
}

export interface PurchaseThisMonthParams {
  /** 可选，Ym / Y-m，默认当月（后端当前固定当月） */
  month?: string;
}

/** 查询本科室本月采购计划情况 */
export function getPurchaseThisMonth(params: PurchaseThisMonthParams = {}) {
  return post<PurchaseThisMonthResponse>("/platform/purchase/thismonth", params);
}

export interface PurchaseTemplateResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  url?: string;
  /** 下载展示文件名：科室名称 + 统计周期 + 采购计划表模板.xlsx */
  filename?: string;
  sheet_count?: number;
  department_id?: number | string;
  department_name?: string;
  start_time?: string;
  end_time?: string;
}

export interface PurchaseTemplateParams {
  /** 目标科室：is_divide 账号必填 */
  department_id?: number | string;
  /** 下单数量统计起始时间 Y-m-d 或 Y-m-d H:i:s；与 end_time 成对传入，均不传默认上个月 */
  start_time?: string;
  /** 下单数量统计结束时间 Y-m-d 或 Y-m-d H:i:s */
  end_time?: string;
}

/** 下载采购计划 Excel 模板（计划数量预填统计周期内采购数量，默认上个月） */
export function downloadPurchaseTemplate(params: PurchaseTemplateParams = {}) {
  return get<PurchaseTemplateResponse>("/platform/purchase/template", params);
}

export interface PurchaseSettingItem {
  category_id?: number | string;
  goods_id?: number | string;
  spec_id?: number | string;
  sku?: string;
  /** 类型 5 可按行指定账号 */
  account_id?: number | string;
  /** 计划数量（必填，须大于 0） */
  quantity: number;
  price?: number | string;
  amount?: number | string;
  remark?: string;
}

export interface PurchaseSettingParams {
  /** YYYY-MM 或 Ym */
  month: string;
  /**
   * 采购计划类型
   * 1全院限购 2科室限购 3个人限购
   */
  control_type: number | string;
  /** 目标科室：类型 2/3 必填；类型 1 全院可不传 */
  department_id?: number | string;
  /** 类型 3 个人限购（也可在 items 内传） */
  account_id?: number | string;
  remark?: string;
  /** append 追加/更新；overwrite 覆盖同 control_type */
  mode?: "append" | "overwrite";
  items: PurchaseSettingItem[];
}

export interface PurchaseSettingResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
  data?: {
    flow_id?: number | string;
    apply_month?: string;
    department_id?: number | string;
    department_name?: string;
    control_type?: number | string;
    control_type_text?: string;
    account_id?: number | string;
    is_new_flow?: boolean;
    mode?: string;
    imported_count?: number;
    updated_count?: number;
    deleted_count?: number;
    item_count?: number;
  };
}

/** 采购计划设置 / 创建（按 control_type 写入 flow_items） */
export function createPurchasePlan(params: PurchaseSettingParams) {
  return post<PurchaseSettingResponse>("/platform/purchase/create", params);
}

/** @deprecated 请使用 createPurchasePlan */
export function savePurchasePlanSetting(params: PurchaseSettingParams) {
  return createPurchasePlan(params);
}

export interface PurchaseSubmitParams {
  /** YYYY-MM 或 Ym */
  month: string;
  file: File;
  /**
   * 采购控制类型（必填）
   * 1全院限购 2科室限购 3个人限购
   * 默认 2 科室限购
   */
  control_type?: number | string;
  /** 目标科室：is_divide 账号必填；类型 2/3 */
  department_id?: number | string;
  /** 类型 3 个人限购必填 */
  account_id?: number | string;
  /** 可选，限定导入分类范围 */
  category_id?: number | string;
  remark?: string;
  /** 修改已有审批中计划时传入 */
  flow_id?: number | string;
  /** append 增量同步；overwrite 整表覆盖（修改时默认 overwrite） */
  mode?: "append" | "overwrite";
}

export interface PurchaseSubmitResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
  data?: {
    flow_id?: number | string;
    apply_month?: string;
    category_id?: number | string;
    control_type?: number | string;
    control_type_text?: string;
    account_id?: number | string;
    goods_count?: number;
    mode?: string;
  };
}

/** 提交采购计划（multipart 上传；须 POST，PHP PUT 不解析 $_FILES） */
export function submitPurchasePlan(params: PurchaseSubmitParams) {
  const form = new FormData();
  form.append("month", params.month);
  form.append("file", params.file);
  form.append("control_type", String(params.control_type ?? 2));
  if (params.department_id != null && params.department_id !== "") {
    form.append("department_id", String(params.department_id));
  }
  if (params.account_id != null && params.account_id !== "") {
    form.append("account_id", String(params.account_id));
  }
  if (params.category_id != null && params.category_id !== "") {
    form.append("category_id", String(params.category_id));
  }
  if (params.remark?.trim()) {
    form.append("remark", params.remark.trim());
  }
  if (params.flow_id != null && params.flow_id !== "") {
    form.append("flow_id", String(params.flow_id));
  }
  if (params.mode) {
    form.append("mode", params.mode);
  }
  return upload<PurchaseSubmitResponse>("/platform/purchase/submit", form);
}

export interface PurchaseApproveParams {
  flow_id: number | string;
  /** true 同意，false 拒绝 */
  is_agree: boolean;
  remark?: string;
}

export interface PurchaseApproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
}

/** 采购计划审批 */
export function approvePurchasePlan(params: PurchaseApproveParams) {
  return put<PurchaseApproveResponse>("/platform/purchase/approve", params);
}

export interface PurchaseCancelParams {
  flow_id: number | string;
  remark: string;
}

export interface PurchaseCancelResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
}

/** 取消采购计划（未审批 / 审批中） */
export function cancelPurchasePlan(params: PurchaseCancelParams) {
  return put<PurchaseCancelResponse>("/platform/purchase/cancel", params);
}

export interface PurchaseUnapproveParams {
  flow_id: number | string;
  remark?: string;
}

export interface PurchaseUnapproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
  data?: {
    flow_id?: number | string;
    status?: number;
    status_text?: string;
  };
}

/** 采购计划反审（仅已通过） */
export function unapprovePurchasePlan(params: PurchaseUnapproveParams) {
  return put<PurchaseUnapproveResponse>("/platform/purchase/unapprove", params);
}

export interface PurchaseDeleteResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
}

/** 删除采购计划：已取消；采购计划管理员在未处理/审批中也可删 */
export function deletePurchasePlan(id: number | string) {
  return put<PurchaseDeleteResponse>(`/platform/purchase/delete/${id}`);
}

export interface PurchasePlanItemUpdateParams {
  /** wz_flow_items.id */
  id: number | string;
  quantity?: number;
  /** 0 = 不限定规格 */
  spec_id?: number;
  goods_id?: number | string;
  price?: number;
  remark?: string;
}

export interface PurchasePlanItemUpdateResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
  data?: PurchasePlanGoodsRow;
}

/** 修改采购计划明细（规格 / 不限定规格 / 计划数） */
export function updatePurchasePlanItem(params: PurchasePlanItemUpdateParams) {
  return put<PurchasePlanItemUpdateResponse>("/platform/purchase/update", params);
}

export interface PurchasePlanItemDeleteResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
}

/** 删除采购计划明细（wz_flow_items.id） */
export function deletePurchasePlanItem(id: number | string) {
  return put<PurchasePlanItemDeleteResponse>("/platform/purchase/item/delete", { id });
}

/** 全院/其它科室同物资计划明细行 */
export interface PurchaseHospitalItemRow extends PurchasePlanGoodsRow {
  flow_status?: number | string;
  flow_status_text?: string | null;
  apply_month?: string | number;
  apply_month_text?: string | null;
}

export interface PurchaseHospitalItemListParams {
  goods_id: number | string;
  /** 0 = 不限定规格 */
  spec_id?: number | string;
  /** 当前采购项目 wz_flow_items.id */
  flow_item_id: number | string;
}

export interface PurchaseHospitalItemListResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: PurchaseHospitalItemRow[];
  goods?: {
    id?: number | string;
    title?: string | null;
    m_pic?: string | null;
    code?: string | null;
    spec_id?: number | string;
    spec_name?: string | null;
    apply_month?: string | number;
    apply_month_text?: string | null;
  };
}

/** 全院/其它科室相同物资、相同规格或不限定规格的计划项目列表 */
export function getPurchaseHospitalItemList(params: PurchaseHospitalItemListParams) {
  return get<PurchaseHospitalItemListResponse>("/platform/purchase/item/list", {
    goods_id: params.goods_id,
    flow_item_id: params.flow_item_id,
    spec_id: params.spec_id != null ? params.spec_id : 0,
  });
}
